import React from "react";
import ReactDOM from "react-dom/client";
import Root from "./routes/Root";
import { RouteErrorPage } from "./routes/pages/RouteErrorPage";
import AssessmentTwoToneIcon from "@mui/icons-material/AssessmentTwoTone";
import MovieTwoToneIcon from "@mui/icons-material/MovieTwoTone";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import SupportTwoToneIcon from "@mui/icons-material/SupportTwoTone";
import SettingsSuggestTwoToneIcon from "@mui/icons-material/SettingsSuggestTwoTone";
import { createBrowserRouter, redirect, RouterProvider } from "react-router-dom";
import type { LoaderFunctionArgs } from "react-router-dom";
import axios from "axios";
import { Auth, checkIfCollaboratorIsAuthenticated } from "./auth/auth";
import { Configurations } from "./routes/pages/config";
import { BasicInfo, Collaborators, IAM, Organization, Payments } from "./routes/pages/org";
import localforage from "localforage";
import { checkPermission, RolePermissions } from "./components/PermissionsContext";
import { Page404 } from "./routes/pages/404";
import { InvitePage } from "./routes/pages/invite";
import { Ads, Hall, Radio, TV } from "./routes/pages/ads";
import { Negotiate, NegotiateHistory, Ranking } from "./routes/pages/negotiate";
import { cfg } from "./runtimeConfig";
import { PainelPage } from "./routes/pages/painel";
import { Support } from "./routes/pages/support";
import { AccessRequestPage } from "./routes/pages/access-request";
import { RepresentativeVerificationPage } from "./routes/pages/representative-verification";
import { Page403 } from "./routes/pages/403";
import { LandingPage } from "./routes/pages/landing";
import { PrivacyPage, TermsPage } from "./routes/pages/legal";
import { negotiateAPI, onRTAReconnect, RTA, sessionInfo } from "./services/negotiationBridge";
import { negotiateLoader } from "./routes/loaders/negotiateLoader";
import { paymentMethodGate, requireOrgPaymentReadiness } from "./routes/loaders/paymentMethodGate";
import "./index.css";
import { setup401Interceptor } from "./auth/enterprise401";

export const enterpriseAPI = axios.create({
  baseURL: cfg.REACT_APP_API_URL,
});

export const paymentAPI = axios.create({
  baseURL: cfg.REACT_APP_API_PAYMENT_URL,
});

export const adsAPI = axios.create({
  baseURL: cfg.REACT_APP_API_ADS_URL,
});

export const compressorAPI = axios.create({
  baseURL: cfg.REACT_APP_API_MEDIA_COMPRESSOR,
});

export const notifyAPI = axios.create({
  baseURL: cfg.REACT_APP_API_NOTIFY_URL,
});

// Support thread API — fronts /support/* on enterprise.dakasa.me, which the
// Ingress forwards to tartaro-operations:8093. Same JWT, same origin as
// enterpriseAPI, just a different prefix.
export const supportAPI = axios.create({
  baseURL: cfg.REACT_APP_API_SUPPORT_URL,
});

export { negotiateAPI, onRTAReconnect, RTA, sessionInfo };

// ---- 401 Interceptor for all enterprise API instances ----
// When any API call gets a 401, try to refresh the session and retry once.
setup401Interceptor(enterpriseAPI);
setup401Interceptor(paymentAPI);
setup401Interceptor(adsAPI);
setup401Interceptor(compressorAPI);
setup401Interceptor(notifyAPI);
setup401Interceptor(supportAPI);

// ---- End 401 Interceptor ----

const router = createBrowserRouter(
  [
    {
      path: "/",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (isLogged) {
          return redirect("/painel");
        }
        return false;
      },
      element: <LandingPage />,
    },
    {
      path: "painel",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (isLogged) return false;

        return redirect("/auth");
      },
      element: (
        <Root
          nav={{
            title: "Painel",
            icon: <AssessmentTwoToneIcon sx={{ fontSize: "var(--space-9)" }} />,
          }}
          sidebar={{
            activateItem: "painel",
          }}
          page={<PainelPage />}
        />
      ),
    },
    {
      path: "anuncios",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (!isLogged) {
          return redirect("/auth");
        }

        const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
        if (isInTutorial) {
          return redirect("/anuncios/radio");
        }

        const orgHasAvatar = (await localforage.getItem("orgHasAvatar")) as boolean | undefined;
        if (!orgHasAvatar) {
          return redirect("/organizacao/info");
        }

        const [weekly, hall] = await Promise.all([
          requireOrgPaymentReadiness("weekly"),
          requireOrgPaymentReadiness("hall"),
        ]);
        if (weekly === "signed_out" || hall === "signed_out") return redirect("/auth");

        const permissions = (await localforage.getItem("permissions")) as RolePermissions | null;
        if (!permissions) return redirect("/auth");
        if (weekly === "ready" && checkPermission(permissions.roomRadioAds, "view")) {
          return redirect("/anuncios/radio");
        }

        if (weekly === "ready" && checkPermission(permissions.roomTvAds, "view")) {
          return redirect("/anuncios/tv");
        }

        if (hall === "ready" && checkPermission(permissions.hallAds, "view")) {
          return redirect("/anuncios/atrio");
        }

        return redirect("/403");
      },
    },
    {
      path: "anuncios",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (!isLogged) {
          return redirect("/auth");
        }

        const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
        if (isInTutorial) {
          return false;
        }

        const orgHasAvatar = (await localforage.getItem("orgHasAvatar")) as boolean | undefined;
        if (!orgHasAvatar) {
          return redirect("/organizacao/info");
        }

        const paymentGate = await paymentMethodGate("any");
        if (paymentGate) return paymentGate;

        const permissions = (await localforage.getItem("permissions")) as RolePermissions | null;
        if (!permissions) return redirect("/auth");
        if (checkPermission(permissions.roomRadioAds, "view")) {
          return false;
        }

        if (checkPermission(permissions.roomTvAds, "view")) {
          return false;
        }

        if (checkPermission(permissions.hallAds, "view")) {
          return false;
        }

        return redirect("/403");
      },
      element: (
        <Root
          nav={{
            title: "Anúncios",
            icon: <MovieTwoToneIcon sx={{ fontSize: "var(--space-9)" }} />,
          }}
          sidebar={{
            activateItem: "ads",
          }}
          page={<Ads />}
        />
      ),
      children: [
        {
          path: "radio",
          loader: async () => {
            const isLogged = await checkIfCollaboratorIsAuthenticated();
            if (!isLogged) {
              return redirect("/auth");
            }

            const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
            if (isInTutorial) {
              return false;
            }

            const orgHasAvatar = (await localforage.getItem("orgHasAvatar")) as boolean | undefined;
            if (!orgHasAvatar) {
              return redirect("/organizacao/info");
            }

            const paymentGate = await paymentMethodGate();
            if (paymentGate) return paymentGate;

            const permissions = (await localforage.getItem(
              "permissions",
            )) as RolePermissions | null;
            if (!permissions) return redirect("/auth");
            if (checkPermission(permissions.roomRadioAds, "view")) {
              return false;
            }

            return redirect("/403");
          },
          element: <Radio />,
        },
        {
          path: "tv",
          loader: async () => {
            const isLogged = await checkIfCollaboratorIsAuthenticated();
            if (!isLogged) {
              return redirect("/auth");
            }

            const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
            if (isInTutorial) {
              return false;
            }

            const orgHasAvatar = (await localforage.getItem("orgHasAvatar")) as boolean | undefined;
            if (!orgHasAvatar) {
              return redirect("/organizacao/info");
            }

            const paymentGate = await paymentMethodGate();
            if (paymentGate) return paymentGate;

            const permissions = (await localforage.getItem(
              "permissions",
            )) as RolePermissions | null;
            if (!permissions) return redirect("/auth");
            if (checkPermission(permissions.roomTvAds, "view")) {
              return false;
            }

            return redirect("/403");
          },
          element: <TV />,
        },
        {
          path: "atrio",
          loader: async () => {
            const isLogged = await checkIfCollaboratorIsAuthenticated();
            if (!isLogged) {
              return redirect("/auth");
            }

            const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
            if (isInTutorial) {
              return false;
            }

            const orgHasAvatar = (await localforage.getItem("orgHasAvatar")) as boolean | undefined;
            if (!orgHasAvatar) {
              return redirect("/organizacao/info");
            }

            const paymentGate = await paymentMethodGate("hall");
            if (paymentGate) return paymentGate;

            const permissions = (await localforage.getItem(
              "permissions",
            )) as RolePermissions | null;
            if (!permissions) return redirect("/auth");
            if (checkPermission(permissions.hallAds, "view")) {
              return false;
            }

            return redirect("/403");
          },
          element: <Hall />,
        },
      ],
    },
    {
      path: "negociar",
      loader: negotiateLoader,
      element: <Root page={<Negotiate />} />,
    },
    {
      path: "negociar/ranking",
      loader: negotiateLoader,
      element: <Root page={<Ranking />} />,
    },
    {
      path: "negociar/historico",
      loader: negotiateLoader,
      element: <Root page={<NegotiateHistory />} />,
    },
    {
      path: "suporte",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (isLogged) return false;

        return redirect("/auth");
      },
      element: (
        <Root
          nav={{
            title: "Suporte",
            icon: <SupportTwoToneIcon sx={{ fontSize: "var(--space-9)" }} />,
          }}
          sidebar={{
            activateItem: "suporte",
          }}
          page={<Support />}
        />
      ),
    },
    {
      path: "organizacao",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (!isLogged) {
          return redirect("/auth");
        }

        const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
        if (isInTutorial) {
          return redirect("/organizacao/info");
        }

        const permissions = (await localforage.getItem("permissions")) as RolePermissions | null;
        if (!permissions) return redirect("/auth");
        if (checkPermission(permissions.orgInfos, "view")) {
          return redirect("/organizacao/info");
        }

        if (checkPermission(permissions.orgCollaborators, "view")) {
          return redirect("/organizacao/colaboradores");
        }

        if (checkPermission(permissions.orgRoles, "view")) {
          return redirect("/organizacao/iam");
        }

        if (checkPermission(permissions.orgPayments, "view")) {
          return redirect("/organizacao/pagamentos");
        }

        return redirect("/403");
      },
    },
    {
      path: "organizacao",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (!isLogged) {
          return redirect("/auth");
        }

        const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
        if (isInTutorial) {
          return false;
        }

        const permissions = (await localforage.getItem("permissions")) as RolePermissions | null;
        if (!permissions) return redirect("/auth");
        if (checkPermission(permissions.orgInfos, "view")) {
          return false;
        }

        if (checkPermission(permissions.orgCollaborators, "view")) {
          return false;
        }

        if (checkPermission(permissions.orgRoles, "view")) {
          return false;
        }

        if (checkPermission(permissions.orgPayments, "view")) {
          return false;
        }

        return redirect("/403");
      },
      element: (
        <Root
          nav={{
            title: "Organização",
            icon: <BusinessTwoToneIcon sx={{ fontSize: "var(--space-9)" }} />,
          }}
          sidebar={{
            activateItem: "organizacao",
          }}
          page={<Organization />}
        />
      ),
      children: [
        {
          path: "info",
          loader: async () => {
            const isLogged = await checkIfCollaboratorIsAuthenticated();
            if (!isLogged) {
              return redirect("/auth");
            }

            const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
            if (isInTutorial) {
              return false;
            }

            const permissions = (await localforage.getItem(
              "permissions",
            )) as RolePermissions | null;
            if (!permissions) return redirect("/auth");
            if (checkPermission(permissions.orgInfos, "view")) {
              return false;
            }

            return redirect("/403");
          },
          element: <BasicInfo />,
        },
        {
          path: "colaboradores",
          loader: async () => {
            const isLogged = await checkIfCollaboratorIsAuthenticated();
            if (!isLogged) {
              return redirect("/auth");
            }

            const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
            if (isInTutorial) {
              return false;
            }

            const permissions = (await localforage.getItem(
              "permissions",
            )) as RolePermissions | null;
            if (!permissions) return redirect("/auth");
            if (checkPermission(permissions.orgCollaborators, "view")) {
              return false;
            }

            return redirect("/403");
          },
          element: <Collaborators />,
        },
        {
          path: "pagamentos",
          loader: async () => {
            const isLogged = await checkIfCollaboratorIsAuthenticated();
            if (!isLogged) {
              return redirect("/auth");
            }

            const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
            if (isInTutorial) {
              return false;
            }

            const permissions = (await localforage.getItem(
              "permissions",
            )) as RolePermissions | null;
            if (!permissions) return redirect("/auth");
            if (checkPermission(permissions.orgPayments, "view")) {
              return false;
            }

            return redirect("/403");
          },
          element: <Payments />,
        },
        {
          path: "iam",
          loader: async () => {
            const isLogged = await checkIfCollaboratorIsAuthenticated();
            if (!isLogged) {
              return redirect("/auth");
            }

            const isInTutorial = (await localforage.getItem("inTutorial")) as boolean | undefined;
            if (isInTutorial) {
              return false;
            }

            const permissions = (await localforage.getItem(
              "permissions",
            )) as RolePermissions | null;
            if (!permissions) return redirect("/auth");
            if (checkPermission(permissions.orgRoles, "view")) {
              return false;
            }

            return redirect("/403");
          },
          element: <IAM />,
        },
      ],
    },
    {
      path: "configuracoes",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (isLogged) return false;

        return redirect("/auth");
      },
      element: (
        <Root
          nav={{
            title: "Configurações",
            icon: <SettingsSuggestTwoToneIcon sx={{ fontSize: "var(--space-9)" }} />,
          }}
          sidebar={{
            activateItem: "configuracoes",
          }}
          page={<Configurations />}
        />
      ),
    },
    {
      path: "auth",
      loader: async () => {
        const isLogged = await checkIfCollaboratorIsAuthenticated();
        if (!isLogged) return false;

        return redirect("/painel");
      },
      element: <Auth />,
    },
    {
      path: "invite/:inviteID",
      loader: async ({ params }: LoaderFunctionArgs) => {
        const inviteID = params.inviteID;
        if (!inviteID) return redirect("/404");

        // The one-shot token, not this public route parameter, is the authority.
        // Let InvitePage validate it with bounded retry semantics: a transient
        // network failure must never turn a newly approved owner into a 404.
        return false;
      },
      element: <InvitePage />,
    },
    {
      path: "solicitar-acesso",
      element: <AccessRequestPage />,
    },
    {
      path: "verificar-representante",
      element: <RepresentativeVerificationPage />,
    },
    {
      path: "403",
      element: <Page403 />,
    },
    {
      // Public legal pages — no auth loader, standalone reader (no sidebar shell).
      path: "termos",
      element: <TermsPage />,
    },
    {
      path: "privacidade",
      element: <PrivacyPage />,
    },
    {
      path: "*",
      element: <Page404 />,
    },
  ].map((route) => ({ ...route, errorElement: <RouteErrorPage /> })),
);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
